import type { CheckoutLineItem, Paddle, PaddleEventData } from "@paddle/paddle-js";
import { initializePaddle } from "@paddle/paddle-js";
import { LogSentryException } from "@shapr3d/frontend-lib";

import { paddleBillingClientToken, paddleBillingEnvironment } from "../config";

export type PaddleBillingEventCallback = (event: PaddleEventData) => void;

export class PaddleBillingAdapter {
  private static instance: PaddleBillingAdapter = new PaddleBillingAdapter();

  static getInstance(): PaddleBillingAdapter {
    return this.instance;
  }

  private loadPromise: Promise<void> | undefined;
  private eventCallback: PaddleBillingEventCallback | undefined;
  private paddle: Paddle | undefined;

  setEventCallback(callback: PaddleBillingEventCallback): void {
    this.eventCallback = callback;
  }

  async load(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = (async () => {
      try {
        const paddle = await initializePaddle({
          environment: paddleBillingEnvironment,
          token: paddleBillingClientToken,
          eventCallback: (event: PaddleEventData) => {
            this.eventCallback?.(event);
          },
        });

        if (!paddle) {
          throw new Error("Paddle.js v2 failed to initialize");
        }

        this.paddle = paddle;
      } catch (error) {
        LogSentryException({
          messagePrefix: "Paddle Billing error",
          error,
          level: "info",
        });
        this.loadPromise = undefined;
        throw error;
      }
    })();

    return this.loadPromise;
  }

  openInlineCheckout({
    customerId,
    priceId,
    containerSelector,
    user: { userId, email, countryCode },
  }: {
    customerId?: string;
    priceId: string;
    containerSelector: string;
    user: {
      userId: string;
      email: string;
      countryCode: string;
    };
  }): void {
    if (!this.paddle) {
      throw new Error("Paddle.js v2 is not loaded");
    }

    this.paddle.Checkout.open({
      settings: {
        allowLogout: false,
        allowedPaymentMethods: ["alipay", "wechat_pay", "card"],
        showAddDiscounts: false,
        variant: "one-page",
        displayMode: "inline",
        frameTarget: containerSelector,
        frameInitialHeight: 450,
        frameStyle: "width: 100%; background-color: transparent; border: none;",
      },
      customer: customerId
        ? {
            id: customerId,
            address: {
              countryCode,
            },
          }
        : {
            email,
            address: {
              countryCode,
            },
          },
      items: [{ priceId, quantity: 1 }],
      customData: { user_id: userId },
    });
  }

  updateCheckoutItems(items: CheckoutLineItem[]): void {
    this.paddle?.Checkout.updateItems(items);
  }

  closeCheckout(): void {
    this.paddle?.Checkout.close();
  }
}
